"use client";

import React, { createContext, useContext, useState, useEffect } from "react";
import LABELS, { LabelKey } from "../lib/data/labels";
import {
  appGetItem,
  appSetItem,
  subscribeStorageAuthState,
} from "@/lib/auth-storage";

/**
 * Language type - either 'en' (English) or 'zh' (Chinese)
 */
type Language = "en" | "zh";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  translate: (key: LabelKey) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const STORAGE_KEY = "jupas_language";

// Read initial language from the in-memory app store before first render.
const getInitialLanguage = (): Language => {
  if (typeof window !== 'undefined') {
    const stored = appGetItem(STORAGE_KEY);
    if (stored === "zh" || stored === "en") {
      return stored;
    }
  }
  return "en";
};

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<Language>(getInitialLanguage());
  const [mounted, setMounted] = useState(false);

  // Set mounted flag and re-check storage after mount
  useEffect(() => {
    setMounted(true);
    const stored = appGetItem(STORAGE_KEY);
    if (stored === "zh" || stored === "en") {
      if (stored !== language) {
        setLanguageState(stored);
      }
    }
  }, [language]);

  // Re-read when sign-in state changes in case the in-memory value was updated.
  useEffect(() => {
    return subscribeStorageAuthState(() => {
      const stored = appGetItem(STORAGE_KEY);
      if (stored === "zh" || stored === "en") {
        setLanguageState(stored);
      }
    });
  }, []);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    appSetItem(STORAGE_KEY, lang);
  };

  /**
   * Translation function - translates a label key to the current language
   * @param key - The label key from LABELS object
   * @returns Translated string in current language
   * 
   * @example
   * translate('recommended') // Returns "Recommended" or "推薦" based on current language
   * translate('favourites')  // Returns "Favourites" or "最愛課程"
   */
  const translate = (key: LabelKey): string => {
    return LABELS[key]?.[language] || key;
  };

  // Don't render children until mounted on client to avoid SSR mismatch
  if (!mounted) {
    return null;
  }

  return (
    <LanguageContext.Provider value={{ language, setLanguage, translate }}>
      {children}
    </LanguageContext.Provider>
  );
}

/**
 * Hook to access language context
 * @returns {LanguageContextType} Language context with current language, setLanguage function, and translate() translation function
 * 
 * @example
 * const { language, setLanguage, translate } = useLanguage();
 * const label = translate('recommended'); // "Recommended" or "推薦"
 * setLanguage('zh'); // Switch to Chinese
 */
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}

/**
 * Standalone translation function that can be used outside of React components
 * For React components, prefer using the translate() function from useLanguage() hook
 *
 * @example
 * import { translate } from '@/lib/utils/translate';
 * const label = translate('recommended'); // Uses the current in-memory language
 * const labelZh = translate('recommended', 'zh'); // Force Chinese translation
 */

export { translate } from "../lib/utils/translate";
